import React from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { DATE_FORMAT, SelectedOption, StatusType } from '../../api/api.types';
import {
  DeskOpeningExceptionModel, daysOpenOptions, DeskOpeningExceptionRequest, OutOfHoursType, OutOfHoursTypeOptions, OpenHoursExceptionModel
} from '../../api/core/core.types';
import Select from '../forms/Select';
import TimeSelect from '../forms/TimeSelect';
import { DeskOpeningEditActions } from './DeskOpeningEditModal';
import dayjs, {Dayjs} from 'dayjs';
import DatePicker from '../forms/DatePicker';

interface DeskOpeningExceptionModalProps {
  deskOpeningException?: DeskOpeningExceptionModel;
  toggle: () => void;
  isOpen: boolean;
  action: DeskOpeningEditActions;
  onModalSubmit: (request: DeskOpeningExceptionRequest, action: DeskOpeningEditActions) => void;
}

interface DeskOpeningExceptionModalState {
  date?: string;
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  openHoliday: { value: string; label: string};
  openHoursHoliday: { startTime: string; endTime: string; index: number}[];
}

export default class DeskOpeningExceptionModal extends React.PureComponent<DeskOpeningExceptionModalProps, DeskOpeningExceptionModalState> {

  readonly state = {
    date: dayjs().format(DATE_FORMAT),
    pickupOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
    returnOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
    openHoliday: daysOpenOptions[1],
    openHoursHoliday: [] as { startTime: string; endTime: string; index: number}[],
  };

  componentDidMount() {
    this.setState({
      openHoursHoliday: [ {
        startTime: '700',
        endTime: '1800',
        index: 0
      } ]
    });
  }

  async componentDidUpdate(prevProps: DeskOpeningExceptionModalProps) {
    if (prevProps !== this.props && this.props.isOpen) {
      if (this.props.deskOpeningException) {
        this.updateValues();
        await this.assignOpenHours();

        // eslint-disable-next-line react/no-did-update-set-state
        if (this.props.deskOpeningException.isClosed) this.setState({ openHoliday: daysOpenOptions[0] });
        else {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({ openHoliday: this.props.deskOpeningException.openHoursExceptions.length ? daysOpenOptions[1] : daysOpenOptions[2] });
        }
      }
    }
  }

  updateValues = () => {
    if (this.props.deskOpeningException) {
      this.setState({
        date: this.props.deskOpeningException.date,
        pickupOutOfHoursType: this.props.deskOpeningException.pickupOutOfHoursType,
        returnOutOfHoursType: this.props.deskOpeningException.returnOutOfHoursType,
      });
    }
  };

  assignOpenHours = () => {
    if (this.props.deskOpeningException) {
      const openHours: { startTime: string; endTime: string; index: number}[] = [];

      this.props.deskOpeningException.openHoursExceptions.forEach((oh: OpenHoursExceptionModel, index) => {
        openHours.push({
          startTime: String(oh.startTime),
          endTime: String(oh.endTime),
          index
        });
      });

      this.saveOpenHoursToState(openHours);
    }
  };

  patchField = (option: SelectedOption, field: string) => {
    this.setState({ ...this.state, [field]: option.value });
  };

  onTimeChange = async (value: { startTime?: string; endTime?: string; index: number }, openings: any[]) => {
    const openHours = JSON.parse(JSON.stringify(openings));

    if (value.startTime) {
      openHours[value.index].startTime = value.startTime;
    } else {
      openHours[value.index].endTime = value.endTime;
    }

    await this.saveOpenHoursToState(openHours);
  };

  saveOpenHoursToState = (openHours: any[]) => {
    this.setState({
      openHoursHoliday: openHours
    });
  };

  onDateChange = (date?: Dayjs) => {
    this.setState({
      date: date ? date.format(DATE_FORMAT) : undefined
    });
  };

  onCancel = () => {
    this.reset();
    this.props.toggle();
  };

  reset = () => {
    this.setState({
      date: dayjs().format(DATE_FORMAT),
      pickupOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
      returnOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
      openHoliday: daysOpenOptions[1],
      openHoursHoliday: [] as { startTime: string; endTime: string; index: number; isInvalid?: boolean }[],
    });
  };

  onSubmit = () => {
    if (!this.state.date) {
      return;
    }

    const request: DeskOpeningExceptionRequest = {
      date: this.state.date,
      statusType: StatusType.ACTIVE,
      isClosed: this.state.openHoliday === daysOpenOptions[0],
      pickupOutOfHoursType: this.state.pickupOutOfHoursType as OutOfHoursType,
      returnOutOfHoursType: this.state.returnOutOfHoursType as OutOfHoursType,
      openHoursExceptions: this.getOpenHoursExceptions()
    };

    this.props.onModalSubmit(request, this.props.deskOpeningException ? DeskOpeningEditActions.EDIT : DeskOpeningEditActions.ADD_NEW);
    this.reset();
  };

  getOpenHoursExceptions = () => {
    const openHours: OpenHoursExceptionModel[] = [];

    if (this.state.openHoliday === daysOpenOptions[1]) {
      this.state.openHoursHoliday.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime });
      });
    }

    return openHours;
  };

  removeOpenHours = async (openHoursArray: any[], position: number) => {
    const openings = openHoursArray.filter((oa) => oa.index !== position);

    this.saveOpenHoursToState(openings);
  };

  onAddHoursClick = (openHoursArray: any[]) => {
    const openHours = JSON.parse(JSON.stringify(openHoursArray));
    openHours.push({ startTime: '700', endTime: '1800', index: openHoursArray.length });

    this.saveOpenHoursToState(openHours);
  };

  render() {
    return (
      <Modal style={{ minWidth: '960px' }} isOpen={this.props.isOpen} toggle={this.props.toggle} autoFocus={false}>
        <ModalBody toggle={this.props.toggle}>
          <div className="desk-opening-modal">
            <div className="mb-2 d-flex justify-content-start">
              {this.props.deskOpeningException && (
                <h1>Edit holiday</h1>
              )}
              {!this.props.deskOpeningException && (
              <h1>Add holiday</h1>
              )}
            </div>
            <div>
              <div>Holidays will overrule regular opening hours</div>
              <div>If you have set up supplier website, desk holidays opening hours will be shown in the “Locations and opening hours” section.</div>
            </div>
            <hr />
            <div>
              <div className="ml-1 d-flex row align-items-center justify-content-start">
                <div className="mr-2 text-light">Date:</div>
                <div className="modal-date-picker">
                  <DatePicker
                    isEdit
                    date={this.state.date ? dayjs(this.state.date) : undefined}
                    onChange={(val) => this.onDateChange(val)}
                    isInvalid={!this.state.date}
                  />
                </div>
              </div>
              <div className="d-flex mt-3">
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mr-1 mb-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">{dayjs(this.state.date).format('dddd').toUpperCase()}</label>
                    </div>
                    <div className='d-flex'>
                      <Select
                          value={this.state.openHoliday}
                          className="mb-1"
                          placeholder="Choose opening type"
                          options={daysOpenOptions}
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          label="openMon"
                          onChange={(value: any) => { this.setState({ openHoliday: value }); }}
                      />
                    </div>
                    {this.state.openHoliday === daysOpenOptions[1] && (
                    <>
                      { this.state.openHoursHoliday.map((o) => (
                        <div className="d-flex row-justify-content-start align-items-center" key={`Mon${o.index}`}>
                          <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours(this.state.openHoursHoliday, o.index)}>-</button>
                          <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, this.state.openHoursHoliday)} value={o.startTime} />
                          <div className="margin-left-minimal margin-right-minimal"> - </div>
                          <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, this.state.openHoursHoliday)} value={o.endTime} />
                        </div>
                      ))}
                      <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick(this.state.openHoursHoliday)}>+</button>
                    </>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex row info-container align-items-center mt-2 mb-1">
                <label className="ml-2 mr-1 align-items-center">Out of hours pickup: </label>
                <Select
                  value={OutOfHoursTypeOptions.find((o) => o.value === this.state.pickupOutOfHoursType)}
                  className="mr-1"
                  placeholder="Out of hours pickup"
                  options={OutOfHoursTypeOptions}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="pickupOutOfHoursType"
                  onChange={(value) => this.patchField(value as SelectedOption, 'pickupOutOfHoursType')}
                />
              </div>
              <div className="d-flex row info-container align-items-center">
                <label className="ml-2 d-flex">Out of hours drop-off: </label>
                <Select
                  value={OutOfHoursTypeOptions.find((o) => o.value === this.state.returnOutOfHoursType)}
                  className="mr-1"
                  placeholder="Out of hours return"
                  options={OutOfHoursTypeOptions}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="returnOutOfHoursType"
                  onChange={(value) => this.patchField(value as SelectedOption, 'returnOutOfHoursType')}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div>
            <button type="button" className="btn btn-outline-secondary mr-1" onClick={() => { this.onCancel(); }}>CANCEL</button>
            <button type="button" className="btn btn-primary" onClick={this.onSubmit}>SAVE</button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}
